import { useState, useCallback, useRef, useEffect } from 'react';
import { route } from '../utils/route';
import type { ProductListing } from '../types/products';

interface UseInfiniteProductsProps {
  initialProducts: ProductListing[];
  totalProducts: number;
  perPage: number;
  currentPage: number;
  categorySlug: string;
}

export function useInfiniteProducts({
  initialProducts,
  totalProducts,
  perPage,
  currentPage,
  categorySlug,
}: UseInfiniteProductsProps) {
  const [products, setProducts] = useState<ProductListing[]>(initialProducts);
  const [page, setPage] = useState(currentPage);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(initialProducts.length < totalProducts);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Reset when category changes (Inertia navigation)
  useEffect(() => {
    setProducts(initialProducts);
    setPage(currentPage);
    setHasMore(initialProducts.length < totalProducts);
    setIsLoadingMore(false);
  }, [categorySlug, initialProducts, currentPage, totalProducts]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);

    try {
      const nextPage = page + 1;
      const url = `${route('api.products.index')}?category=${encodeURIComponent(categorySlug)}&page=${nextPage}&per_page=${perPage}`;
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error('Failed to load');

      const data = await response.json();
      setProducts(prev => [...prev, ...data.products]);
      setPage(nextPage);
      setHasMore(data.hasMore);
    } catch {
      // silently fail
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, page, categorySlug, perPage]);

  // IntersectionObserver for sentinel
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '400px' }
    );

    const sentinel = sentinelRef.current;
    if (sentinel && hasMore) {
      observerRef.current.observe(sentinel);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [loadMore, hasMore]);

  return { products, isLoadingMore, hasMore, sentinelRef };
}
